import { useState } from 'react';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Checkbox, Dialog, Field, Input, Select } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { loiTheoTruong } from '../../../../lib/errors';
import { useToast } from '../../../../lib/toast';
import { useTaoNguon } from '../../hooks/useNguon';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import {
  CONG_MAC_DINH,
  MAU_DSN,
  NOI_TRONG,
  PHUT_TOI_DA,
  PHUT_TOI_THIEU,
  cauLenhCapQuyen,
  docDsn,
  dungDsn,
  laUrlApi,
  luocDoMacDinh,
  type ThongTinNoi,
} from '../../nghiep-vu/ket-noi';
import { maNguonDuyNhat } from '../../nghiep-vu/ma-nguon';
import type { AccessMode, CreateSourcePayload, GraphqlSource } from '../../types';
import { CacOKetNoiApi, KHAI_API_MAC_DINH, khoiApi, type KhaiApi } from './CacOKetNoiApi';

export type LoaiNguonMoi = 'dsn' | 'graphql' | 'api';

/**
 * Khai một nguồn mới — DSN, GraphQL hoặc API (port `ThemNguon.tsx` native).
 *
 * - **Mã là giá trị suy ra, không phải trạng thái thứ hai**: suy tại chỗ vẽ từ tên, nên không có gì để trôi (bản cũ ghi
 *   mã vào state mỗi lần gõ tên; tên điền bằng đường không qua `onChange` thì mã RỖNG). Mã hiện chỉ đọc (ADR-UI-0014).
 * - **Năm ô thay cho một chuỗi DSN**: một ký tự sai trong chuỗi cho ra lỗi nói về tên máy chủ; năm ô có nhãn thì mỗi ô
 *   hỏng riêng, ngay cạnh nhãn nói nó là gì. Ô dán chuỗi vẫn còn và tự rã ra thành các ô.
 * - Bỏ khối "nguồn sẽ thuộc phòng ban nào" và ô phạm vi của native: tenant nằm trong phiên, không có trục phòng ban.
 */
export function ThemNguon({
  loai,
  onClose,
  onCreated,
  daCo,
}: {
  /** `null` = hộp đóng. */
  loai: LoaiNguonMoi | null;
  onClose: () => void;
  onCreated: (s: GraphqlSource) => void;
  /** Mọi mã ĐÃ dùng, mọi loại nguồn: `id` duy nhất trên toàn bảng nguồn — lọc theo loại là đề xuất mã trùng rồi ăn 409. */
  daCo: string[];
}) {
  // `key` theo loại: mở lại là một biểu mẫu sạch, không phải dọn tay hai mươi ô như native.
  return loai ? <HopThem key={loai} loai={loai} onClose={onClose} onCreated={onCreated} daCo={daCo} /> : null;
}

function HopThem({
  loai,
  onClose,
  onCreated,
  daCo,
}: {
  loai: LoaiNguonMoi;
  onClose: () => void;
  onCreated: (s: GraphqlSource) => void;
  daCo: string[];
}) {
  const { t } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const tao = useTaoNguon();
  const laDsn = loai === 'dsn';
  const laApi = loai === 'api';

  const [name, setName] = useState('');
  const [dialect, setDialect] = useState('postgres');
  const [noi, setNoi] = useState<ThongTinNoi>(NOI_TRONG);
  const [danDsn, setDanDsn] = useState('');
  const [hienMatKhau, setHienMatKhau] = useState(false);
  const [luocDoTho, setLuocDoTho] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [token, setToken] = useState('');
  const [autoSync, setAutoSync] = useState(false);
  const [phut, setPhut] = useState(60);
  const [cheDo, setCheDo] = useState<AccessMode>('live');
  const [api, setApi] = useState<KhaiApi>(KHAI_API_MAC_DINH);

  // Lịch đồng bộ chỉ có nghĩa khi có bản sao để đồng bộ vào. Nguồn API không có.
  const coBanSao = !laApi && (!laDsn || cheDo === 'copy');
  const ma = maNguonDuyNhat(name, daCo);
  const luocDo = luocDoTho || luocDoMacDinh(dialect);
  const dsn = dungDsn(dialect, noi);
  const dat = (k: keyof ThongTinNoi, v: string) => setNoi((p) => ({ ...p, [k]: v }));
  const loiTruong = loiTheoTruong(tao.error);

  const than = (): CreateSourcePayload =>
    laApi
      ? {
          id: ma,
          name,
          api: khoiApi(api),
          apiSecret: api.xacThuc === 'none' ? null : api.khoaApi || null,
        }
      : laDsn
        ? {
            id: ma,
            name,
            dsn: dsn.trim(),
            dialect,
            luocDo,
            accessMode: cheDo,
            autoSync: coBanSao && autoSync,
            syncIntervalSeconds: phut * 60,
          }
        : {
            id: ma,
            name,
            endpoint: endpoint.trim(),
            graphqlToken: token.trim() || null,
            autoSync,
            syncIntervalSeconds: phut * 60,
          };

  const gui = () =>
    tao.mutate(than(), {
      onSuccess: (s) => {
        toast.success(t('troLyAiKetNoiThem.daKhaiNguon'));
        onCreated(s);
        onClose();
      },
    });

  const dayDu = Boolean(
    ma && name.trim() && (laApi ? laUrlApi(api.apiUrl) : laDsn ? dsn.trim() : endpoint.trim()),
  );

  return (
    <Dialog
      open
      onClose={onClose}
      width={720}
      title={
        laApi
          ? t('troLyAiKetNoiThem.themApi')
          : laDsn
            ? t('troLyAiKetNoiThem.themDsn')
            : t('troLyAiKetNoiThem.themGraphql')
      }
    >
      <div className="space-y-3 pt-2">
        {/* Tên đứng TRƯỚC: nó sinh ra mã. */}
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <Field label={t('troLyAiKetNoiThem.tenHienThi')} required error={loiTruong['/name']}>
            {(f) => (
              <Input
                {...f}
                autoFocus
                placeholder={t('troLyAiKetNoiThem.tenViDu')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          <div className="min-w-0 sm:w-64">
            <p className="mb-1.5 text-sm font-medium text-content-primary">{t('troLyAiKetNoiThem.maNguon')}</p>
            <p
              className="truncate rounded border border-default bg-subtle px-3 py-1.5 font-mono text-sm text-content-secondary"
              title={ma}
              aria-live="polite"
            >
              {ma || t('troLyAiKetNoiThem.maNguonChuaCo')}
            </p>
          </div>
        </div>
        <p className={loiTruong['/id'] ? 'text-xs text-danger-700' : 'text-xs text-content-muted'}>
          {loiTruong['/id'] ?? t('troLyAiKetNoiThem.maNguonGhi')}
        </p>

        {laApi ? (
          <>
            <CacOKetNoiApi gt={api} dat={(thay) => setApi((p) => ({ ...p, ...thay }))} />
            <div className="rounded-lg bg-subtle p-3 text-xs leading-relaxed text-content-secondary">
              <p className="mb-1 font-medium text-content-primary">{t('troLyAiKetNoiThem.apiHopDongTieuDe')}</p>
              <pre className="whitespace-pre-wrap font-mono text-xs text-content-muted">
                {'{\n  "rows": [ { "chi_tieu": "…", "so_tien": 1200 }, … ],\n  "tongHop": { "tong_tai_san": 9800 }\n}'}
              </pre>
              <p className="mt-1.5">{t('troLyAiKetNoiThem.apiHopDongGhi')}</p>
            </div>
          </>
        ) : laDsn ? (
          <>
            <Field
              label={t('troLyAiKetNoiThem.cheDoLabel')}
              hint={cheDo === 'live' ? t('troLyAiKetNoiThem.cheDoTrucTiepGhi') : t('troLyAiKetNoiThem.cheDoSaoChepGhi')}
            >
              {(f) => (
                <Select<AccessMode>
                  {...f}
                  className="w-full"
                  value={cheDo}
                  onChange={setCheDo}
                  options={[
                    { value: 'live', label: t('troLyAiKetNoiThem.cheDoTrucTiep') },
                    { value: 'copy', label: t('troLyAiKetNoiThem.cheDoSaoChep') },
                  ]}
                />
              )}
            </Field>

            <div className="flex flex-wrap gap-3">
              <Field label={t('troLyAiKetNoiThem.loaiCsdlLabel')} className="w-full sm:w-56">
                {(f) => (
                  <Select<string>
                    {...f}
                    className="w-full"
                    value={dialect}
                    onChange={setDialect}
                    options={[
                      { value: 'postgres', label: 'PostgreSQL' },
                      { value: 'mssql', label: 'SQL Server' },
                    ]}
                  />
                )}
              </Field>
              <Field label={t('troLyAiKetNoiThem.luocDoLabel')} hint={t('troLyAiKetNoiThem.luocDoGhi')} className="w-full sm:w-48">
                {(f) => (
                  <Input
                    {...f}
                    placeholder={luocDoMacDinh(dialect)}
                    value={luocDoTho}
                    onChange={(e) => setLuocDoTho(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <div className="flex flex-wrap gap-3">
              <Field label={t('troLyAiKetNoiThem.mayChu')} required className="min-w-[16rem] flex-1">
                {(f) => (
                  <Input
                    {...f}
                    placeholder="may-chu.khach.vn"
                    value={noi.host}
                    onChange={(e) => dat('host', e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('troLyAiKetNoiThem.cong')} hint={t('troLyAiKetNoiThem.congGhi')} className="w-32">
                {(f) => (
                  <Input
                    {...f}
                    inputMode="numeric"
                    placeholder={String(CONG_MAC_DINH[dialect] ?? 5432)}
                    value={noi.cong}
                    onChange={(e) => dat('cong', e.target.value.replace(/\D/g, ''))}
                  />
                )}
              </Field>
            </div>

            <Field label={t('troLyAiKetNoiThem.tenCsdl')} required>
              {(f) => <Input {...f} placeholder="ten_csdl" value={noi.db} onChange={(e) => dat('db', e.target.value)} />}
            </Field>

            <div className="flex flex-wrap gap-3">
              <Field label={t('troLyAiKetNoiThem.taiKhoan')} required className="min-w-[14rem] flex-1">
                {(f) => (
                  <Input
                    {...f}
                    autoComplete="off"
                    placeholder="doc_bao_cao"
                    value={noi.taiKhoan}
                    onChange={(e) => dat('taiKhoan', e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('troLyAiKetNoiThem.matKhau')} className="min-w-[14rem] flex-1">
                {(f) => (
                  <div className="flex items-center gap-2">
                    {/* Hiện được mật khẩu: mật khẩu CSDL hay có ký tự dễ gõ nhầm, ô chấm đen không cho soát lại. */}
                    <Input
                      {...f}
                      type={hienMatKhau ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={noi.matKhau}
                      onChange={(e) => dat('matKhau', e.target.value)}
                    />
                    <Button
                      variant="link"
                      size="sm"
                      aria-label={hienMatKhau ? t('troLyAiKetNoiThem.anMatKhauNhan') : t('troLyAiKetNoiThem.hienMatKhauNhan')}
                      onClick={() => setHienMatKhau((v) => !v)}
                    >
                      {hienMatKhau ? t('troLyAiKetNoiThem.anMatKhau') : t('troLyAiKetNoiThem.hienMatKhau')}
                    </Button>
                  </div>
                )}
              </Field>
            </div>

            {/* Đường dán, để SAU các ô: quản trị CSDL hay gửi nguyên chuỗi qua chat. */}
            <Field label={t('troLyAiKetNoiThem.danDsn')} hint={t('troLyAiKetNoiThem.dsnGhi')}>
              {(f) => (
                <Input
                  {...f}
                  placeholder={MAU_DSN[dialect]}
                  value={danDsn}
                  onChange={(e) => {
                    const tho = e.target.value;
                    setDanDsn(tho);
                    const doc = docDsn(tho);
                    if (!doc) return;
                    if (doc.dialect) setDialect(doc.dialect);
                    setNoi(doc.tt);
                    // Xoá ô dán sau khi rã: chuỗi có mật khẩu nằm lại trên màn hình là vào ảnh chụp màn hình.
                    setDanDsn('');
                  }}
                />
              )}
            </Field>

            {/* Nói ra yêu cầu TRƯỚC khi họ đi xin tài khoản — xin nhầm, hoặc xin thừa quyền ghi. */}
            <div className="rounded-lg bg-subtle p-3 text-xs leading-relaxed text-content-secondary">
              <p className="mb-1 font-medium text-content-primary">{t('troLyAiKetNoiThem.xinQuyen')}</p>
              <pre className="whitespace-pre-wrap font-mono text-xs text-content-muted">
                {cauLenhCapQuyen(dialect, luocDo)}
              </pre>
              {dialect === 'mssql' ? (
                <p className="mt-1.5 flex items-start gap-1.5 text-warning-700">
                  <WarningOutlined aria-hidden className="mt-0.5 shrink-0" />
                  <span>{t('troLyAiKetNoiThem.mssqlCanhBao')}</span>
                </p>
              ) : (
                <p className="mt-1.5 flex items-start gap-1.5 text-success-700">
                  <CheckCircleOutlined aria-hidden className="mt-0.5 shrink-0" />
                  <span>{t('troLyAiKetNoiThem.pgGhi')}</span>
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <Field label={t('troLyAiKetNoiThem.endpoint')} required>
              {(f) => (
                <Input
                  {...f}
                  placeholder="https://khach-hang.vn/graphql"
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('troLyAiKetNoiThem.khoaApi')} hint={t('troLyAiKetNoiThem.khoaApiGhi')}>
              {(f) => (
                <Input {...f} type="password" autoComplete="new-password" value={token} onChange={(e) => setToken(e.target.value)} />
              )}
            </Field>
          </>
        )}

        {coBanSao ? (
          <div className="flex flex-wrap items-end gap-3">
            <Checkbox checked={autoSync} onChange={(e) => setAutoSync(e.target.checked)}>
              {t('troLyAiKetNoiThem.dongBoTheoLich')}
            </Checkbox>
            <Field label={t('troLyAiKetNoiThem.moiPhut')} className="w-32">
              {(f) => (
                <Input
                  {...f}
                  type="number"
                  min={PHUT_TOI_THIEU}
                  max={PHUT_TOI_DA}
                  value={String(phut)}
                  disabled={!autoSync}
                  onChange={(e) => setPhut(Math.min(PHUT_TOI_DA, Math.max(PHUT_TOI_THIEU, Number(e.target.value) || 60)))}
                />
              )}
            </Field>
          </div>
        ) : null}

        <p className="flex items-start gap-1.5 text-xs text-content-secondary">
          <CheckCircleOutlined aria-hidden className="mt-0.5 shrink-0 text-success-700" />
          {laApi
            ? t('troLyAiKetNoiThem.baBuocGhiApi')
            : coBanSao
              ? t('troLyAiKetNoiThem.baBuocGhi')
              : t('troLyAiKetNoiThem.baBuocGhiTrucTiep')}
        </p>

        {tao.error && Object.keys(loiTruong).length === 0 ? (
          <p role="alert" className="rounded border border-danger-100 bg-danger-50 p-3 text-sm text-danger-700">
            {t('troLyAiKetNoiThem.khongTaoDuoc', { loi: moTaLoi(tao.error) })}
          </p>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={onClose}>
          {t('chung.huy')}
        </Button>
        <Button loading={tao.isPending} disabled={!dayDu} onClick={gui}>
          {t('troLyAiKetNoiThem.themNguon')}
        </Button>
      </div>
    </Dialog>
  );
}
